import React, { useState, useEffect, useRef } from 'react';
import { PolymorphicOption } from './types';

interface PolymorphicSelectorProps {
  type: 'Skill' | 'Occupation' | 'Characteristic';
  value: number | null;
  onChange: (id: number | null, name?: string) => void;
  apiBaseUrl: string;
  csrfToken: string;
  label: string;
  required?: boolean;
  error?: string;
}

const PolymorphicSelector: React.FC<PolymorphicSelectorProps> = ({
  type,
  value,
  onChange,
  apiBaseUrl,
  csrfToken,
  label,
  required = false,
  error,
}) => {
  const [options, setOptions] = useState<PolymorphicOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedName, setSelectedName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 初期値の名前を取得
  useEffect(() => {
    if (value && options.length === 0) {
      const searchEndpoint = type === 'Skill'
        ? 'search_skills'
        : type === 'Occupation'
        ? 'search_occupations'
        : 'search_characteristics';

      fetch(`${apiBaseUrl}/${searchEndpoint}?term=`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      })
        .then((res) => res.json())
        .then((data: PolymorphicOption[]) => {
          const selected = data.find((opt) => opt.id === value);
          if (selected) {
            setSelectedName(selected.name);
          }
        })
        .catch(console.error);
    }
  }, [value, type, apiBaseUrl, csrfToken, options.length]);

  // 検索
  useEffect(() => {
    if (searchTerm.length >= 1) {
      const searchEndpoint = type === 'Skill'
        ? 'search_skills'
        : type === 'Occupation'
        ? 'search_occupations'
        : 'search_characteristics';

      fetch(`${apiBaseUrl}/${searchEndpoint}?term=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      })
        .then((res) => res.json())
        .then((data: PolymorphicOption[]) => {
          setOptions(data);
        })
        .catch(console.error);
    } else {
      setOptions([]);
    }
  }, [searchTerm, type, apiBaseUrl, csrfToken]);

  // クリックアウトサイドで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: PolymorphicOption) => {
    onChange(option.id, option.name);
    setSelectedName(option.name);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="mb-3">
      <label className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <div className="position-relative">
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${error ? 'is-invalid' : ''}`}
          value={isOpen ? searchTerm : selectedName}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="検索して選択..."
        />
        {error && <div className="invalid-feedback">{error}</div>}
        {isOpen && options.length > 0 && (
          <div
            ref={dropdownRef}
            className="position-absolute w-100 bg-white border rounded shadow-sm"
            style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
          >
            {options.map((option) => (
              <div
                key={option.id}
                className="px-3 py-2 cursor-pointer hover-bg-light"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelect(option)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
              >
                {option.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PolymorphicSelector;



